/** Crown Institute — Curriculum + Frameworks + Certifications + Continuing Education */
import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import SEOHead from "../components/SEOHead";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import {
  GraduationCap, FileText, Award, BookOpen, ClipboardCheck,
  Brain, Compass, Layers, Shield, ChevronDown, Crown, Briefcase,
  ArrowRight, Calendar, RefreshCw, BadgeCheck, Eye, Ear, Cpu,
  Activity, MessageCircle,
} from "lucide-react";

// =====================================================================
// CURRICULUM PAGE — 8 semesters fetched from /api/portal/curriculum/sections
// (We fetch via /api/academy/sections-public for the list, falling back to the
// authenticated endpoint via portalApi when needed)
// =====================================================================

// Maps semester order → AcademyDetail slug (must mirror SEMESTER_SLUG_TO_ORDER in AcademyDetail.jsx)
const SEMESTER_ORDER_TO_SLUG = {
  1: "civic-duty-foundation",
  2: "american-history-foundation",
  3: "constitution-and-rights",
  4: "public-budget-and-taxation",
  5: "financial-literacy",
  6: "entrepreneurship",
  7: "leadership-and-management",
  8: "community-leadership-internship",
};

export const Curriculum = () => {
  const [sections, setSections] = useState([]);
  const [grading, setGrading] = useState(null);
  const [internship, setInternship] = useState(null);
  const [openIdx, setOpenIdx] = useState(0);

  useEffect(() => {
    // Public read of curriculum — try the open frameworks meta, then the curriculum endpoint
    api.get("/academy/grading").then(r => setGrading(r.data.grading)).catch(() => {});
    api.get("/academy/internship").then(r => setInternship(r.data.internship)).catch(() => {});
    api.get("/academy/curriculum-public").then(r => setSections(r.data.sections || []))
      .catch(() => {
        // Fallback (legacy public route not yet available — render skeleton)
        setSections([]);
      });
  }, []);

  return (
    <Layout>
      <SEOHead
        title="Course Catalog · 8-Semester Curriculum | Crown Institute Academy"
        description="The complete Crown Institute 8-semester curriculum: civic duty, history, constitution, public budget, financial literacy, entrepreneurship, leadership, and a 80-hour internship capstone."
        path="/curriculum" />
      <section className="container-x section-y">
        <div className="text-[11px] uppercase tracking-[0.28em] text-gold flex items-center gap-2">
          <BookOpen size={12} /> Course Catalog
        </div>
        <h1 className="font-display mt-4">8 Semesters · 4 Frameworks · 9.2 Pass Standard</h1>
        <p className="text-cream-dim mt-4 max-w-2xl">
          The complete Crown Institute curriculum integrates civic, financial, and leadership education with the
          CHAT Protocol, Five Pillars, 3+3+1 Decision Model, and Human System Science.
        </p>

        {grading && (
          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            <div className="card-premium glass">
              <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Grading Scale</div>
              <div className="font-display text-2xl text-gold mt-2">{grading.scale}</div>
              <div className="text-cream-dim text-sm mt-1">Pass: <span className="text-gold">{grading.pass_score}</span></div>
            </div>
            <div className="card-premium glass">
              <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Component Weighting</div>
              <ul className="text-cream-dim text-sm mt-2 space-y-1">
                {grading.components.map(c => (
                  <li key={c.name} className="flex justify-between"><span>{c.name}</span><span className="text-gold">{c.weight}%</span></li>
                ))}
              </ul>
            </div>
            <div className="card-premium glass">
              <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Retake Policy</div>
              <p className="text-cream-dim text-sm mt-2">{grading.retake_policy}</p>
            </div>
          </div>
        )}
      </section>

      <section className="bg-navy-deep border-y border-gold-dim/20 section-y">
        <div className="container-x">
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Semesters 1–8</div>
          <h2 className="font-display mt-3">Academic structure</h2>

          {sections.length === 0 && (
            <div className="text-cream-dim text-sm mt-4">Loading curriculum…</div>
          )}

          <div className="mt-8 space-y-3" data-testid="curriculum-semesters">
            {sections.map((s, i) => {
              const open = openIdx === i;
              return (
                <article key={s.id} className="glass overflow-hidden" data-testid={`semester-${s.order}`}>
                  <button onClick={() => setOpenIdx(open ? -1 : i)}
                    className="w-full text-left flex items-center justify-between gap-4 px-5 py-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-gold/10 border border-gold flex items-center justify-center">
                        <span className="font-display text-xl text-gold">{s.order}</span>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Semester {s.order}</div>
                        <h3 className="font-display text-cream">{s.title}</h3>
                      </div>
                    </div>
                    <ChevronDown size={18} className={`text-gold transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && (
                    <div className="px-5 pb-6 pt-2 grid md:grid-cols-2 gap-6 border-t border-gold-dim/10">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-gold mb-2 flex items-center gap-2">
                          <BookOpen size={12} /> Lessons
                        </div>
                        <ul className="space-y-1.5">
                          {(s.lessons || []).map((l, li) => (
                            <li key={li} className="text-cream-dim text-sm flex items-start gap-2">
                              <span className="text-gold mt-1">•</span>{l}
                            </li>
                          ))}
                        </ul>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-gold mt-5 mb-2 flex items-center gap-2">
                          <Layers size={12} /> Frameworks
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {(s.frameworks || []).map(f => (
                            <span key={f} className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded border border-gold-dim/40 text-gold">{f}</span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-electric mb-2 flex items-center gap-2">
                          <FileText size={12} /> Assignment
                        </div>
                        <p className="text-cream text-sm leading-relaxed">{s.assignment || "Capstone project."}</p>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-electric mt-5 mb-2 flex items-center gap-2">
                          <ClipboardCheck size={12} /> Exam Components
                        </div>
                        <ul className="text-cream-dim text-sm space-y-1">
                          {(s.exams || []).map((e, ei) => <li key={ei}>· {e}</li>)}
                        </ul>
                        <div className="mt-5 pt-4 border-t border-gold-dim/15">
                          <Link
                            to={`/academy/${SEMESTER_ORDER_TO_SLUG[s.order] || ""}`}
                            data-testid={`semester-${s.order}-detail-link`}
                            className="inline-flex items-center gap-2 text-electric text-sm hover:text-gold transition-colors"
                          >
                            Open Semester {s.order} page <ArrowRight size={14} />
                          </Link>
                        </div>
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {internship && (
        <section className="container-x section-y">
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Internship Capstone</div>
          <h2 className="font-display mt-3">{internship.total_hours_required}-hour verified internship</h2>
          <div className="grid md:grid-cols-2 gap-6 mt-6">
            <div className="card-premium glass">
              <div className="text-[10px] uppercase tracking-[0.22em] text-gold mb-2">Placement options</div>
              <ul className="text-cream-dim text-sm space-y-1">
                {internship.placement_options.map(p => (
                  <li key={p} className="flex items-start gap-2"><Briefcase size={12} className="text-electric mt-1" />{p}</li>
                ))}
              </ul>
            </div>
            <div className="card-premium glass">
              <div className="text-[10px] uppercase tracking-[0.22em] text-gold mb-2">Documentation requirements</div>
              <p className="text-cream-dim text-sm"><span className="text-cream">Weekly journal:</span> {internship.weekly_journal}</p>
              <p className="text-cream-dim text-sm mt-2"><span className="text-cream">Midpoint check-in:</span> {internship.midpoint_checkin}</p>
              <p className="text-cream-dim text-sm mt-2"><span className="text-cream">Final report:</span> {internship.final_report}</p>
              <p className="text-cream-dim text-sm mt-2"><span className="text-cream">Supervisor evaluation:</span> {internship.supervisor_evaluation}</p>
            </div>
          </div>
        </section>
      )}

      <section className="container-x pb-16">
        <div className="card-premium glass flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="font-display">Ready to enroll?</h3>
            <p className="text-cream-dim text-sm mt-1">Initiation $499 · Semester renewal $99 · No financial barriers via the no-cost civic pathway</p>
          </div>
          <div className="flex gap-3 btn-stack">
            <Link to="/crown-institute-academy#enroll-section" className="contents"><Button className="btn-gold h-12">Enroll Now</Button></Link>
            <Link to="/frameworks" className="contents"><Button className="btn-ghost-gold h-12">Read the Frameworks <ArrowRight size={14} className="ml-2" /></Button></Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};


// =====================================================================
// FRAMEWORKS PAGE
// =====================================================================
export const Frameworks = () => {
  const [fw, setFw] = useState(null);
  useEffect(() => { api.get("/academy/frameworks").then(r => setFw(r.data.frameworks)); }, []);
  if (!fw) return <Layout><section className="container-x section-y text-cream-dim">Loading…</section></Layout>;

  return (
    <Layout>
      <SEOHead
        title="The Four Frameworks | Crown Institute Academy"
        description="CHAT Protocol, Five Pillars, 3+3+1 Decision Model, and Human System Science — the foundational decision frameworks of every Crown Institute course."
        path="/frameworks" />
      <section className="container-x section-y">
        <div className="text-[11px] uppercase tracking-[0.28em] text-gold flex items-center gap-2">
          <Compass size={12} /> Foundational Frameworks
        </div>
        <h1 className="font-display mt-4">CHAT · Five Pillars · 3+3+1 · Human System Science</h1>
        <p className="text-cream-dim mt-4 max-w-2xl">
          Every lesson, exam, and assignment in the Crown Institute curriculum integrates these four frameworks.
          They are not theory — they are the operating system of disciplined civic, financial, and leadership decision-making.
        </p>
      </section>

      {/* CHAT */}
      <section className="bg-navy-deep border-y border-gold-dim/20 section-y">
        <div className="container-x">
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Framework 1</div>
          <h2 className="font-display mt-3 flex items-center gap-3"><Brain size={28} className="text-gold" /> {fw.CHAT.name}</h2>
          <p className="text-cream-dim mt-3 max-w-3xl">{fw.CHAT.summary}</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            {fw.CHAT.steps.map(s => (
              <div key={s.letter} className="card-premium glass" data-testid={`chat-${s.letter}`}>
                <div className="font-display text-5xl text-gold leading-none">{s.letter}</div>
                <div className="font-display text-cream mt-2">{s.name}</div>
                <p className="text-cream-dim text-sm mt-3">{s.definition}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Five Pillars */}
      <section className="container-x section-y">
        <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Framework 2</div>
        <h2 className="font-display mt-3 flex items-center gap-3"><Shield size={28} className="text-gold" /> {fw.FivePillars.name}</h2>
        <p className="text-cream-dim mt-3 max-w-3xl">{fw.FivePillars.summary}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-8">
          {fw.FivePillars.pillars.map((p, i) => (
            <div key={p.name} className="card-premium glass" data-testid={`pillar-${i + 1}`}>
              <div className="text-[10px] uppercase tracking-[0.22em] text-gold">{i + 1}</div>
              <div className="font-display text-cream mt-2">{p.name}</div>
              <p className="text-cream-dim text-sm mt-3">{p.definition}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 3+3+1 */}
      <section className="bg-navy-deep border-y border-gold-dim/20 section-y">
        <div className="container-x">
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Framework 3</div>
          <h2 className="font-display mt-3 flex items-center gap-3"><Layers size={28} className="text-gold" /> {fw.ThreeThreeOne.name}</h2>
          <p className="text-cream-dim mt-3 max-w-3xl">{fw.ThreeThreeOne.summary}</p>
          <div className="grid md:grid-cols-3 gap-4 mt-8">
            {[
              { title: "3 Inputs", items: fw.ThreeThreeOne.inputs, tone: "text-electric" },
              { title: "3 Processes", items: fw.ThreeThreeOne.processes, tone: "text-gold" },
              { title: "1 Output", items: [fw.ThreeThreeOne.output], tone: "text-[color:var(--auditiq-success)]" },
            ].map((c, i) => (
              <div key={i} className="card-premium glass">
                <div className={`font-display text-2xl ${c.tone}`}>{c.title}</div>
                <ul className="mt-3 space-y-1.5">
                  {c.items.map(t => <li key={t} className="text-cream-dim text-sm">· {t}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Human System Science */}
      <section className="container-x section-y">
        <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Framework 4</div>
        <h2 className="font-display mt-3 flex items-center gap-3"><Cpu size={28} className="text-gold" /> {fw.HumanSystemScience.name}</h2>
        <p className="text-cream-dim mt-3 max-w-3xl">{fw.HumanSystemScience.summary}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
          {fw.HumanSystemScience.components.map((c, i) => {
            const ICONS = { Brain: Brain, Ears: Ear, Eyes: Eye, Mind: Activity, Mouth: MessageCircle, Behavior: Compass };
            const I = ICONS[c.name] || Brain;
            return (
              <div key={c.name} className="card-premium glass">
                <I size={20} className="text-gold" />
                <div className="font-display text-cream mt-3">{c.name}</div>
                <p className="text-cream-dim text-sm mt-2">{c.function}</p>
              </div>
            );
          })}
        </div>
        <div className="card-premium glass mt-6 border-l-2 border-gold">
          <div className="text-[10px] uppercase tracking-[0.22em] text-gold">Core principle</div>
          <p className="text-cream mt-2">{fw.HumanSystemScience.core_principle}</p>
        </div>
      </section>
    </Layout>
  );
};


// =====================================================================
// CERTIFICATIONS PAGE
// =====================================================================
export const Certifications = () => {
  const [certs, setCerts] = useState([]);
  useEffect(() => { api.get("/academy/certifications").then(r => setCerts(r.data.certifications)); }, []);
  return (
    <Layout>
      <SEOHead
        title="Certifications | Crown Institute Academy"
        description="Five Crown Institute certifications: Student (CICS), Entrepreneur (CICE), Leader (CICL), Municipal Professional (CICMP), and AI Command Specialist (CICAI)."
        path="/certifications" />
      <section className="container-x section-y">
        <div className="text-[11px] uppercase tracking-[0.28em] text-gold flex items-center gap-2">
          <Award size={12} /> Certification Authority
        </div>
        <h1 className="font-display mt-4">Five University-Grade Certifications</h1>
        <p className="text-cream-dim mt-4 max-w-2xl">
          Each Crown Institute certification carries a 24-month validity period and a continuing education requirement.
          The 9.2 pass standard ensures every credential earner is a recognized practitioner.
        </p>
      </section>

      <section className="bg-navy-deep border-y border-gold-dim/20 section-y">
        <div className="container-x grid md:grid-cols-2 gap-5">
          {certs.map(c => (
            <article key={c.id} className="card-premium glass flex flex-col" data-testid={`cert-${c.code}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-[0.22em] text-gold font-mono">{c.code}</span>
                <BadgeCheck size={20} className="text-gold" />
              </div>
              <h3 className="font-display mt-3 text-cream">{c.title}</h3>
              <p className="text-cream-dim text-sm mt-3">{c.purpose}</p>
              <div className="text-[10px] uppercase tracking-[0.22em] text-electric mt-5 mb-2">Requirements</div>
              <ul className="text-cream-dim text-sm space-y-1.5 flex-1">
                {c.requirements.map((r, i) => (
                  <li key={i} className="flex items-start gap-2"><span className="text-gold mt-0.5">•</span>{r}</li>
                ))}
              </ul>
              <div className="mt-5 pt-4 border-t border-gold-dim/15 flex items-center justify-between text-xs">
                <span className="text-cream-dim"><RefreshCw size={11} className="inline mr-1" /> Renews every <span className="text-gold">{c.validity_months} mo</span></span>
                <span className="text-cream-dim"><Calendar size={11} className="inline mr-1" /> {c.ceu_required} CEU/yr</span>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="container-x section-y">
        <div className="card-premium glass flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="font-display">Ready to earn your credential?</h3>
            <p className="text-cream-dim text-sm mt-1">Begin with the 8-semester foundation — CICS is your gateway to all advanced tracks.</p>
          </div>
          <Link to="/crown-institute-academy#enroll-section" className="contents">
            <Button className="btn-gold h-12">Begin CICS Enrollment <ArrowRight size={14} className="ml-2" /></Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};


// =====================================================================
// CONTINUING EDUCATION PAGE
// =====================================================================
export const ContinuingEducation = () => {
  const [ceu, setCeu] = useState(null);
  useEffect(() => { api.get("/academy/ceu").then(r => setCeu(r.data)); }, []);
  return (
    <Layout>
      <SEOHead
        title="Continuing Education | Crown Institute Academy"
        description="Annual continuing education requirements for Crown Institute certificate holders — 12 hours per year across leadership, technology, compliance, financial, and AI literacy updates."
        path="/continuing-education" />
      <section className="container-x section-y">
        <div className="text-[11px] uppercase tracking-[0.28em] text-gold flex items-center gap-2">
          <RefreshCw size={12} /> Continuing Education
        </div>
        <h1 className="font-display mt-4">Stay current. Stay credentialed.</h1>
        <p className="text-cream-dim mt-4 max-w-2xl">
          Every Crown Institute certification requires annual CEU completion to remain active.
          Total annual requirement: <span className="text-gold font-semibold">{ceu?.total_annual_hours || 12} hours</span> across five categories.
        </p>
      </section>

      <section className="bg-navy-deep border-y border-gold-dim/20 section-y">
        <div className="container-x">
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric mb-3">Annual CEU Categories</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {(ceu?.ceu || []).map(c => (
              <div key={c.category} className="card-premium glass text-center" data-testid={`ceu-${c.category.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="font-display text-3xl text-gold">{c.annual_hours}<span className="text-sm text-cream-dim ml-1">hrs</span></div>
                <div className="text-cream mt-2 text-sm">{c.category}</div>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-5 mt-8">
            <div className="card-premium glass">
              <div className="text-[10px] uppercase tracking-[0.22em] text-gold mb-2">Delivery</div>
              <p className="text-cream-dim text-sm">{ceu?.delivery}</p>
            </div>
            <div className="card-premium glass">
              <div className="text-[10px] uppercase tracking-[0.22em] text-gold mb-2">Cost</div>
              <p className="text-cream-dim text-sm">Active students: <span className="text-cream">{ceu?.cost_active_students}</span></p>
              <p className="text-cream-dim text-sm mt-2">Credential-only: <span className="text-cream">{ceu?.cost_credential_only}</span></p>
            </div>
          </div>
        </div>
      </section>

      <section className="container-x pb-16">
        <div className="card-premium glass border-l-2 border-gold">
          <h3 className="font-display">Recertification Process</h3>
          <p className="text-cream-dim mt-3">
            Certifications renew every 24 months. Holders must complete 24 cumulative CEU hours in the 24-month window,
            submit proof of CEU completion, and confirm continued adherence to the Crown Institute Code of Professional Conduct.
            A small recertification fee applies (waived for active students with current Semester Renewal).
          </p>
        </div>
      </section>
    </Layout>
  );
};
